// 多语言 AI 翻译 —— 集中式工具
//
// 现有 batch_translate 一次只接受一个 toLanguage，故"翻成多种语言"= 对每个目标各调一次。
// 产品当前需求：只从中文母版翻译到其它语言（zh → en / ja / ...），不做反向/交叉翻译。
// 但源语言做成参数（默认 'zh'）贯穿，未来若要"按当前语言翻译"，只需在调用处传入即可，写回逻辑无需再改。
import { batch_translate } from "@api/team";
import { getkey } from "@util/utils";
import { getTeamLangCodes } from "@util/teamLang";
import { SUPPORTED_CODES } from "@util/langConfig";

const pendingTranslations = new Set();

const nextMacrotask = () => new Promise((resolve) => setTimeout(resolve, 0));

// 切语言/提交前：先失焦当前编辑元素（触发中文编辑的失焦翻译），再等待所有进行中的翻译完成。
// waitForPendingTranslations 的首个宏任务已给 blur 后的 React render/effect 留出启动翻译的机会。
export async function blurActiveAndWaitTranslations() {
  if (typeof document !== "undefined") document.activeElement?.blur?.();
  await waitForPendingTranslations();
}

// 保存/入库前统一等待页面中所有通过 translateToTargets 发起的失焦翻译。
// 先让一个宏任务，使 blur 导致的 React render/effect 有机会启动翻译；每轮翻译结束后
// 再让一个宏任务，使调用方的 .then/await 写回 ml 字段和父级 state 完成提交。
export async function waitForPendingTranslations() {
  let idleRounds = 0;
  while (idleRounds < 2) {
    await nextMacrotask();
    if (!pendingTranslations.size) {
      idleRounds += 1;
      continue;
    }
    idleRounds = 0;
    await Promise.allSettled([...pendingTranslations]);
  }
}

// 目标语言 = 团队已开通语言 ∩ 前端支持语言 − 源语言
export const getTargetLangs = (fromLanguage = "zh") =>
  getTeamLangCodes().filter((c) => c !== fromLanguage && SUPPORTED_CODES.includes(c));

// 对每个目标语言各调一次 batch_translate（并发），返回按语言分组的译文：
//   { en: { key: 译文 }, ja: { key: 译文 } }
// 单个目标失败只影响该语言（返回空），不影响其它。
export function translateToTargets({
  keyContentMap,
  fromLanguage = "zh",
  teamId,
  targets,
}) {
  const tId = teamId || getkey("clientId");
  const tos = targets && targets.length ? targets : getTargetLangs(fromLanguage);
  const hasVal =
    keyContentMap && Object.keys(keyContentMap).some((k) => keyContentMap[k]);
  if (!tos.length || !hasVal) return Promise.resolve({});
  const task = (async () => {
    const byLang = {};
    await Promise.all(
      tos.map(async (to) => {
        try {
          const res = await batch_translate({
            fromLanguage,
            toLanguage: to,
            teamId: tId,
            keyContentMap,
          });
          byLang[to] = res?.success ? res.result?.keyContentMap || {} : {};
        } catch (e) {
          console.log("translateToTargets error", to, e);
          byLang[to] = {};
        }
      })
    );
    return byLang;
  })();
  pendingTranslations.add(task);
  task.finally(() => pendingTranslations.delete(task));
  return task;
}

// 把"源文 + 各目标语言译文"写进一个 ml 对象，返回新对象：
//   { [fromLanguage]: sourceText, en: 译文, ja: 译文, ... }
// byLang: translateToTargets 的返回；key: 要从每个语言 map 中取的字段名。
export function applyMlTranslations(
  mlObj = {},
  { fromLanguage = "zh", sourceText, byLang = {}, key }
) {
  const out = { ...mlObj };
  if (sourceText != null) out[fromLanguage] = sourceText;
  Object.keys(byLang).forEach((lang) => {
    const v = key != null ? byLang[lang]?.[key] : byLang[lang];
    if (v != null) out[lang] = v;
  });
  return out;
}

// 单值中→多目标翻译，供超管后台等无团队上下文的场景使用（显式指定 targets，
// 不依赖 getTeamLangCodes，避免因当前浏览器无团队/语言配置而静默不翻译）。
// 返回 { [targetCode]: 译文 }，失败或空值的目标不出现在结果里。
export async function translateZhToTargets(text, targets) {
  if (!text || !targets?.length) return {};
  const byLang = await translateToTargets({
    keyContentMap: { value: text },
    fromLanguage: "zh",
    targets,
  });
  const out = {};
  targets.forEach((code) => {
    const v = byLang?.[code]?.value;
    if (v) out[code] = v;
  });
  return out;
}
